import { useEffect, useState, useMemo } from "react";
import { useApiContext } from 'context/ApiContext';

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Switch from "@mui/material/Switch";
import 'leaflet/dist/leaflet.css';

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDDatePicker from "components/MDDatePicker";
import MDTypography from "components/MDTypography";
import MDMapLeaflet from "components/MDMapLeaflet";
import MDMapLibre from "components/MDMapLibre";
import MDSwitch from "components/MDSwitch";

// Material Dashboard 2 React example components
import TurbineDashboardLayout from "examples/LayoutContainers/DashboardLayout";
import TurbineDashboardNavbar from "examples/Navbars/DashboardNavbar";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DataTable from "examples/Tables/DataTableV2";

function TurbineDashboard() {
    const { APIRequestType, alertsTuD, cameraOrientationTuD, fromDateTuD, getAPI, imageTuD, imageUrlTuD, parkTuD, postAPI, selectedAlertIDTuD, setAlertsTuD,
        setCameraOrientationTuD, setFromDateTuD, setImageTuD, setImageUrlTuD, setParkTuD, setSelectedAlertIDTuD, setToDateTuD, setTopActivityTuD,
        setTurbineTuD, setVideoTuD, setVideoUrlTuD, toDateTuD, topActivityTuD, turbineTuD, videoTuD, videoUrlTuD } = useApiContext();
    const [selectedRow, setSelectedRow] = useState(null);
    const [alerts, setAlerts] = useState(null);  // L'état des alertes est géré ici

    const [turbineIDTuD, setTurbineIDTuD] = useState(null);  // Utiliser un etat local pour stocker l'ID de l'eolienne
    const [parkIDTuD, setParkIDTuD] = useState(null);  // Utiliser un etat local pour stocker l'ID du parc
    const [loading, setLoading] = useState(true);

    // Charger l'ID du parc depuis localStorage
    useEffect(() => {
        const savedTurbineID = localStorage.getItem('selectedTurbineID');
        const savedParkID = localStorage.getItem('selectedParkID');
        if (!savedTurbineID || !savedParkID) return;
        setLoading(true);  // Declenchement du chargement

        console.log(" 0 0 1   TuD");
        setTurbineIDTuD(savedTurbineID);  // Mettre a jour l'etat avec l'ID de la turbine recuperee
        setParkIDTuD(savedParkID);  // Mettre a jour l'etat avec l'ID de la turbine recuperee
    }, []);  // L'effet se declenche uniquement au chargement initial du composant

    useEffect(() => {
        if (!turbineIDTuD) return;  // Evite un appel API inutile si l'ID de la turbine n'est pas encore charge
        console.log(" 0 0 2   TuD");
        getAPI(APIRequestType.GET_TURBINE);
        getAPI(APIRequestType.GET_TURBINE_ALERTS);
    }, [turbineIDTuD]);

    useEffect(() => {
        if (!parkIDTuD) return;  // Evite un appel API inutile si l'ID de la turbine n'est pas encore charge
        console.log(" 0 0 2 bis   TuD");
        getAPI(APIRequestType.GET_PARK);
    }, [parkIDTuD]);

    useEffect(() => {
        if (!turbineIDTuD) return;  // Evite un appel API inutile si l'ID de la turbine n'est pas encore charge
        console.log(" 0 0 3   TuD");
        getAPI(APIRequestType.GET_TURBINE_ALERTS);
    }, [fromDateTuD, toDateTuD]);

    useEffect(() => {
        if (!alertsTuD) return;  // Evite un appel API inutile si aucune alerte n'est chargee
        console.log(" 0 0 4   TuD");

        setAlerts(alertsTuD);

        // Selectionne automatiquement la premiere ligne quand les alertes sont chargees
        if (alertsTuD.length > 0) {
            setSelectedRow(0);

            const lastAlert = alertsTuD[0];

            const lastAlertCamera = lastAlert.camera;
            const lastAlertCameraOrientation = lastAlertCamera.orientation;
            setCameraOrientationTuD(lastAlertCameraOrientation);
            setSelectedAlertIDTuD(lastAlert.id);

            const lastAlertId = lastAlert.id;
            getAPI(APIRequestType.GET_IMAGE, lastAlertId);
            getAPI(APIRequestType.GET_VIDEO, lastAlertId);
        }
        setLoading(false);  // Fin du chargement lorsque les alertes sont chargees
    }, [alertsTuD]);

    useEffect(() => {
        console.log(" CHANGE SELECTED ROW : ", selectedRow);
    }, [selectedRow]);

    useEffect(() => {
        return () => {
            resetTuD();
        };
    }, []);

    const resetTuD = () => {
        setTurbineTuD(null);
        setParkTuD(null);
        setAlertsTuD([]);
        setSelectedAlertIDTuD(null);
        setImageTuD(null);
        setImageUrlTuD("");
        setVideoTuD(null);
        setVideoUrlTuD("");
        setCameraOrientationTuD(null);
        setTopActivityTuD("");
        setSelectedRow(null);
    };

    // Tableau de points GPS a afficher sur la carte
    const points = useMemo(() => {
        if (!turbineTuD || !turbineTuD.position) return [];  // Verification si l'objet existe

        const [lat, lng] = turbineTuD.position.split(',').map(coord => parseFloat(coord.trim()));
        return [{
            lat,
            lng,
            id: turbineTuD.id,
            popupText: "empty",
        }];
    }, [turbineTuD]);

    // Selected alert: drives the camera cone (real azimuth) and the detection marker on the map.
    const selectedAlert = useMemo(
        () => (alertsTuD || []).find((alert) => alert.id === selectedAlertIDTuD) || null,
        [alertsTuD, selectedAlertIDTuD]
    );
    const selectedDetection = useMemo(() => {
        if (!selectedAlert || selectedAlert.latitude == null || selectedAlert.longitude == null) return null;
        return {
            lat: Number(selectedAlert.latitude),
            lng: Number(selectedAlert.longitude),
            distance: selectedAlert.distance,
            label: selectedAlert.activity?.type || `Detection ${selectedAlert.id}`,
        };
    }, [selectedAlert]);

    const handleFromDateChange = (date) => {
        setFromDateTuD(date || null);
        if (date && toDateTuD && date > toDateTuD) {
            setToDateTuD(date);
        }
    };

    const handleToDateChange = (date) => {
        setToDateTuD(date || null);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);

        // Récupérer l'heure locale en fonction du fuseau horaire
        /*const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        const ss = String(date.getSeconds()).padStart(2, '0');
        console.log("    Date locale : ", `${yyyy}-${mm}-${dd} . ${hh}:${min}:${ss}`, "  Date UTC : ", `${yyyy2}-${mm2}-${dd2} . ${hh2}:${min2}:${ss2}`);*/

        // Garder la date UTC (temps universel coordonné)
        const yyyy2 = date.getUTCFullYear();
        const mm2 = String(date.getUTCMonth() + 1).padStart(2, '0');
        const dd2 = String(date.getUTCDate()).padStart(2, '0');
        const hh2 = String(date.getUTCHours()).padStart(2, '0');
        const min2 = String(date.getUTCMinutes()).padStart(2, '0');
        const ss2 = String(date.getUTCSeconds()).padStart(2, '0');
        return `${yyyy2}-${mm2}-${dd2} . ${hh2}:${min2}:${ss2}`;
    };

    const handleUpdateAlert = (falseAlarmState, alertId) => {
        // Mettre à jour l'état local des alertes
        setAlerts((prevAlerts) =>
            prevAlerts.map((alert) =>
                alert.id === alertId ? { ...alert, falseAlarm: falseAlarmState } : alert
            )
        );

        console.log("Modifié falseAlarm state:", falseAlarmState, "pour l'alerte ID:", alertId);

        // Appeler l'API pour mettre à jour l'alerte sur le backend
        postAPI({
            requestType: APIRequestType.UPDATE_ALERT,
            alertId: alertId,
            falseAlarm: falseAlarmState, // Envoyer la nouvelle valeur
        });
    };

    return (
        <TurbineDashboardLayout>
            <TurbineDashboardNavbar />
            {/* Affichage de l'ecran de chargement */}
            {loading ? (
                <MDBox display="flex" justifyContent="center" alignItems="center" style={{ height: '100vh' }}>
                    <CircularProgress size={60} />
                </MDBox>
            ) : (
                <MDBox display="flex" flexDirection="column">
                    {/* Date Pickers and Turbine Information */}
                    <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={3} mt={-0.5} mb={1} ml={-0.5}>
                        <MDBox display="flex" alignItems="center" gap={0}>
                            {/* Date Picker "From" */}
                            <MDDatePicker
                                label="From"
                                value={fromDateTuD}
                                onChange={handleFromDateChange}
                                scale={0.9}
                            />

                            {/* Date Picker "To" */}
                            <MDDatePicker
                                label="To"
                                value={toDateTuD}
                                onChange={handleToDateChange}
                                minDate={fromDateTuD} // Empêche de choisir une date antérieure
                                scale={0.9}
                            />
                        </MDBox>

                        <MDBox display="flex" flexDirection="column" alignItems="flex-end" mr={2.5}>
                            {turbineTuD?.number && (
                                <MDTypography variant="button"
                                    fontWeight="bold"
                                    color="dark"
                                    ml={1}
                                    mb={0.5}
                                    fontSize="1.2rem">
                                    E{turbineTuD.number}
                                </MDTypography>
                            )}
                            {turbineTuD?.position && (
                                <MDTypography variant="button"
                                    fontWeight="light"
                                    color="dark"
                                    ml={1}
                                    mb={-0.5}
                                    fontSize="0.8rem">
                                    {turbineTuD.position}
                                </MDTypography>
                            )}
                            {parkTuD?.name && (
                                <MDTypography variant="button"
                                    fontWeight="bold"
                                    color="dark"
                                    ml={1}
                                    fontSize="1rem">
                                    {parkTuD.name}
                                </MDTypography>
                            )}
                        </MDBox>
                    </MDBox>

                    {/* Alerts Display */}
                    <MDBox py={2}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6} lg={3}>
                                <MDBox mb={0.5}>
                                    {alertsTuD.length > 1 && (
                                        <>
                                            <ComplexStatisticsCard
                                                color="info"
                                                icon="crisis_alert"
                                                title="Detections"
                                                count={alertsTuD.length}
                                                lines={1}
                                            />
                                        </>
                                    )}
                                    {alertsTuD.length < 2 && (
                                        <>
                                            <ComplexStatisticsCard
                                                color="info"
                                                icon="crisis_alert"
                                                title="Detection"
                                                count={alertsTuD.length}
                                                lines={1}
                                            />
                                        </>
                                    )}
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <MDBox mb={0.5}>
                                    <ComplexStatisticsCard
                                        color="info"
                                        icon="agriculture"
                                        title="Top Activity"
                                        count={topActivityTuD}
                                        lines={2}
                                    />
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>

                    {/* No Detection Message */}
                    <MDBox display="flex" alignItems="center" justifyContent="center" >
                        {alertsTuD.length === 0 && (
                            <>
                                <MDTypography variant="button"
                                    fontWeight="bold"
                                    color="secondary"
                                    opacity={0.4}
                                    ml={1}
                                    fontSize="5rem">
                                    No Detection
                                </MDTypography>
                            </>
                        )}
                    </MDBox>

                    {/* Detection Details */}
                    <MDBox display="flex" alignItems="center" justifyContent="center" >
                        {alertsTuD.length > 0 && turbineTuD?.number && parkTuD?.name && (
                            <>
                                <MDTypography variant="button"
                                    fontWeight="light"
                                    color="dark"
                                    ml={1}
                                    fontSize="0.8rem">
                                    E{turbineTuD.number} ({parkTuD.name}) - Detection ID {selectedAlertIDTuD}
                                </MDTypography>
                            </>
                        )}
                    </MDBox>

                    {/* Map Display */}
                    <MDBox pb={3}>
                        {alertsTuD.length > 0 && parkTuD?.distance && (
                            <>
                                <MDMapLibre mapType="turbineDashboard" points={points} cameraOrientation={cameraOrientationTuD}
                                    circleRadius={parkTuD.distance}
                                    cameraAzimuth={selectedAlert?.camera?.azimuth ?? null}
                                    cameraFieldOfView={selectedAlert?.camera?.fieldOfView ?? null}
                                    detection={selectedDetection} />
                            </>
                        )}
                    </MDBox>

                    {/* Image/Video Display */}
                    {(imageTuD || videoTuD) && alertsTuD.length > 0 && (
                        <MDBox position="relative" mb={3}>
                            <Grid container spacing={3}>
                                <>
                                    {imageTuD && (
                                        <Grid item xs={12} md={6} lg={6} display="flex" alignItems="center">
                                            <img
                                                src={imageUrlTuD}
                                                width="100%"
                                                style={{
                                                    maxHeight: "28rem", // Assure que l image ne depasse pas la hauteur max
                                                    borderRadius: "12px",
                                                    objectFit: "contain",
                                                }}
                                            />
                                        </Grid>
                                    )}
                                    {videoTuD && (
                                        <Grid item xs={12} md={6} lg={6} display="flex" alignItems="center">
                                            <video
                                                key={videoUrlTuD}
                                                width="100%"
                                                style={{
                                                    maxHeight: "28rem",
                                                    borderRadius: "12px"
                                                }}
                                                controls
                                            >
                                                {videoUrlTuD && <source src={videoUrlTuD} type="video/mp4" />}
                                                Video not supported.
                                            </video>
                                        </Grid>
                                    )}
                                </>
                            </Grid>
                        </MDBox>
                    )}

                    {/* Data Table */}
                    <MDBox pt={3} pb={3}>
                        {alertsTuD.length > 0 && (
                            <>
                                <Grid container spacing={6}>
                                    <Grid item xs={12}>
                                        <Card>
                                            <MDBox
                                                mx={2}
                                                mt={-3}
                                                py={1.5}
                                                px={2}
                                                variant="gradient"
                                                bgColor="info"
                                                borderRadius="lg"
                                                coloredShadow="info"
                                            >
                                                <MDTypography variant="h6" color="white">
                                                    Turbine Detections
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox pt={3}>
                                                <DataTable
                                                    table={{
                                                        columns: [
                                                            { Header: "Detection ID", accessor: "alertid", align: "left" },
                                                            { Header: "Camera", accessor: "camera", align: "center" },
                                                            { Header: "Date", accessor: "date", align: "left" },
                                                            { Header: "Activity", accessor: "activity", align: "center" },
                                                            { Header: "Distance", accessor: "distance", align: "center" },
                                                            { Header: "Bridage", accessor: "bridage", align: "center" },
                                                            { Header: "False Alarm", accessor: "falsealarm", align: "center" },
                                                        ],
                                                        rows: alerts.map((alert, index) => {
                                                            return {
                                                                alertidValue: alert.id,
                                                                cameraValue: alert.camera.orientation,
                                                                dateValue: formatDate(alert.date),
                                                                activityValue: alert.activityId,
                                                                distanceValue: alert.distance,
                                                                bridageValue: alert.bridage ? "YES" : "NO",
                                                                falsealarmValue: alert.falseAlarm, // Display the switch state here

                                                                alertid: (
                                                                    <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
                                                                        {alert.id}
                                                                    </MDTypography>
                                                                ),
                                                                camera: (
                                                                    <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
                                                                        {"C" + alert.camera.orientation}
                                                                    </MDTypography>
                                                                ),
                                                                date: (
                                                                    <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
                                                                        {formatDate(alert.date)}
                                                                    </MDTypography>
                                                                ),
                                                                activity: (
                                                                    <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
                                                                        {alert.activity.type}
                                                                    </MDTypography>
                                                                ),
                                                                distance: (
                                                                    <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
                                                                        {alert.distance + " m"}
                                                                    </MDTypography>
                                                                ),
                                                                bridage: (
                                                                    <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
                                                                        {alert.bridage ? "YES" : "NO"}
                                                                    </MDTypography>
                                                                ),
                                                                falsealarm: (
                                                                    <MDSwitch
                                                                        checked={alert.falseAlarm} // L'état initial du Switch
                                                                        onClick={(e) => e.stopPropagation()} // Empêche la ligne de capter le clic
                                                                        onChange={() => {
                                                                            handleUpdateAlert(!alert.falseAlarm, alert.id); // Met à jour l'état de l'alerte et envoie la requête API
                                                                        }}
                                                                    />
                                                                ),
                                                                index: index,
                                                            };
                                                        }),
                                                    }}
                                                    isSorted={false}
                                                    entriesPerPage={false}
                                                    showTotalEntries={false}
                                                    noEndBorder
                                                    selectedRowIndex={selectedRow} // Passe l'index selectionne
                                                    setSelectedRowIndex={(row) => {
                                                        setSelectedRow(row); // Passe la fonction de mise a jour
                                                    }} // Passe la fonction de mise a jour
                                                />
                                            </MDBox>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </MDBox>
                </MDBox>
            )}
        </TurbineDashboardLayout>
    );
}

export default TurbineDashboard;


